<template>
  <AuthLayout>
    <template #header>
      <Button
        @click="handleBack"
        variant="paper-primary"
        size="sm"
        class="hidden lg:flex absolute -top-12"
      >
        <template #icon>
          <Icon name="ChevronLeft" class="h-4 w-4" />
        </template>
      </Button>
      <h1 class="text-[32px] font-semibold">Reset Password</h1>
    </template>

    <div class="w-full">
      <!-- Error message -->
      <Transition
        enter-from-class="opacity-0 translate-x-4"
        enter-to-class="opacity-100 translate-x-0"
        enter-active-class="transition ease-in-out duration-300"
      >
        <div v-if="submitError" class="w-full mb-4 text-error flex items-center space-x-2">
          <Icon
            name="TriangleAlert"
            :stroke-width="2"
            class="text-error w-4 h-4 mb-1 min-w-4 min-h-4"
          />
          <p class="whitespace-pre-line">{{ submitError }}</p>
        </div>
      </Transition>
      <!-- Form Content -->
      <div class="w-full">
        <!-- Wrapped in a <form> so Safari/iOS treats the two fields as a
             password + confirmation pair and auto-fills BOTH with the
             generated strong password. -->
        <form class="flex flex-col gap-3 mb-8 w-full" @submit.prevent="handleSubmitChangePassword">
          <div>
            <BaseInput
              v-model="password"
              label="Password (required)"
              place_holder="Enter your password"
              field_type="string"
              variant="secondary"
              is_secure_field
              is_required
              autocomplete="new-password"
              :password_rules="passwordRules"
              @focus="typingPassword = true"
              @keydown.space.prevent
              @keydown.enter="handleSubmitChangePassword"
            />

            <p
              v-if="attemptedSubmit && !password"
              class="mt-3 ml-3 font-inter text-error text-sm flex items-center"
            >
              <Icon name="Asterisk" class="mr-2 h-[14px] w-[14px] -skew-x-12 stroke-error" />
              Password is required
            </p>

            <p
              v-else-if="typingPassword"
              class="mt-3 ml-3 font-inter text-sm flex items-center flex-wrap"
            >
              <Icon
                v-if="!allCriteriaMet"
                name="Asterisk"
                class="mr-2 h-[14px] w-[14px] -skew-x-12 stroke-error shrink-0"
              />
              <Icon v-else name="Check" class="mr-2 h-[14px] w-[14px] stroke-success shrink-0" />
              <span
                class="whitespace-nowrap font-inter"
                :class="getCriteriaClass(passwordCriteria.length)"
              >
                12 Characters
              </span>
              <span class="text-strong mx-1">&middot;</span>
              <span
                class="whitespace-nowrap font-inter"
                :class="getCriteriaClass(passwordCriteria.uppercase)"
              >
                1 Uppercase Letter
              </span>
              <span class="text-strong mx-1">&middot;</span>
              <span
                class="whitespace-nowrap font-inter"
                :class="getCriteriaClass(passwordCriteria.number)"
              >
                1 Number
              </span>
            </p>
          </div>

          <div>
            <BaseInput
              v-model="confirmPassword"
              label="Confirm Password (required)"
              place_holder="Enter your password again"
              field_type="string"
              variant="secondary"
              is_secure_field
              is_required
              autocomplete="new-password"
              :password_rules="passwordRules"
              @keydown.space.prevent
              @keydown.enter="handleSubmitChangePassword"
            />

            <div
              v-if="attemptedSubmit && !confirmPassword"
              class="mt-3 ml-3 font-inter text-error text-sm flex items-center"
            >
              <Icon name="Asterisk" class="mr-2 h-[14px] w-[14px] -skew-x-12 stroke-error" />
              Confirm Password is required
            </div>

            <div
              v-else-if="password !== confirmPassword && confirmPassword"
              class="mt-3 ml-3 font-inter text-error text-sm flex items-center"
            >
              <Icon name="Asterisk" class="mr-2 h-[14px] w-[14px] -skew-x-12 stroke-error" />
              Passwords do not match
            </div>
          </div>
        </form>

        <div class="flex flex-col items-center w-full gap-4">
          <Button
            @click="handleSubmitChangePassword"
            @keydown.enter="handleSubmitChangePassword"
            variant="primary-grey"
            size="md"
            class="w-full"
          >
            <template v-if="is_loading" #label>
              <IconPulseSpinner class="h-6 w-6 stroke-white" />
            </template>
            <template v-else #label>Submit</template>
          </Button>
        </div>
      </div>
      <!-- Turnstile widget -->
      <div class="mt-8" data-theme="light" id="turnstile"></div>
    </div>
  </AuthLayout>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useAuthStore, useUIStore } from "@/stores";
import { BaseInput, Button, Icon } from "@/components";
import { IconPulseSpinner } from "@/components/icons";
import { useTurnstile } from "@/composables/useTurnstile";
import AuthLayout from "./AuthLayout.vue";

const { resetTurnstile } = useTurnstile();

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const UIStore = useUIStore();

// Password-generation hint for browsers/keychains that honor `passwordrules`
// (Safari/Keychain, 1Password). Chrome/Google Password Manager ignores it.
// We no longer require a special character — Chrome's generator won't include
// one, and forcing it created signup friction. Length + upper/lower/digit is
// aligned with NIST SP 800-63B (favor length over composition rules).
const passwordRules =
  "minlength: 12; required: lower; required: upper; required: digit;";

const password = ref("");
const confirmPassword = ref("");
const submitError = ref("");
const typingPassword = ref(false);
const attemptedSubmit = ref(false);
const is_loading = ref(false);
const passwordCriteria = ref({
  length: false,
  uppercase: false,
  number: false,
});

onMounted(() => {
  window.scrollTo(0, 0);
  validatePassword();
  if (!route.query.token) {
    router.push("/sign-in");
  }
});

const allCriteriaMet = computed(() => {
  return (
    passwordCriteria.value.length &&
    passwordCriteria.value.uppercase &&
    passwordCriteria.value.number
  );
});

watch(
  () => password.value,
  () => {
    validatePassword();
    matchPasswords();
  }
);

watch(
  () => confirmPassword.value,
  () => matchPasswords()
);

const passwordsMatch = ref(true);

const isFormValid = computed(() => {
  return (
    passwordCriteria.value.length &&
    passwordCriteria.value.uppercase &&
    passwordCriteria.value.number &&
    passwordsMatch.value &&
    password.value &&
    confirmPassword.value
  );
});

function validatePassword() {
  const pwd = password.value;
  passwordCriteria.value.length = pwd.length >= 12;
  passwordCriteria.value.uppercase = /[A-Z]/.test(pwd);
  passwordCriteria.value.number = /\d/.test(pwd);
}

function getCriteriaClass(isValid) {
  return isValid ? "text-success" : "text-error";
}

function matchPasswords() {
  passwordsMatch.value = password.value === confirmPassword.value;
}

async function handleSubmitChangePassword() {
  attemptedSubmit.value = true;

  try {
    if (!authStore.is_turnstile_valid) {
      submitError.value =
        "Please complete the security check below to confirm that you are a human.";
      return;
    }

    if (!isFormValid.value) return;

    is_loading.value = true;
    const token = route.query.token;
    submitError.value = "";

    const changePasswordResult = await authStore.resetPassword(token, password.value);

    if (changePasswordResult && changePasswordResult.tokenExpired) {
      UIStore.animateNotificationAlert({
        message: "Your link is expired or invalid! Please request a new reset.",
        type: "error",
        does_persist: true,
      });
    } else if (changePasswordResult && !changePasswordResult.tokenExpired) {
      UIStore.animateNotificationAlert({
        message: "Password has been Successfully updated!",
        icon: "Key",
      });
      router.push("/sign-in");
    }
  } catch (err) {
    submitError.value = "Error changing password. Please try again.";
  } finally {
    is_loading.value = false;
  }
}

function handleBack() {
  router.push("/sign-in");
}
</script>
